import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { Case } from '../../types/Case/Case.type';
import { Statute } from '../../types/Statute.type';
import { DisparityStage } from '../../types/enums/DisparityStage.enum';
import { DisparitySubType } from '../../types/enums/DisparitySubType.enum';
import { DisparityType } from '../../types/enums/DisparityType.enum';
import { capitalizeFirstLetter } from '../../utils/capitalizeFirstLetter';
import { isSameDayDate, isUnknownStartDate, UNKNOWN_START_DATE } from '../../utils/disparityDates';
import { EnhancementDyad } from '../../utils/enhancementDyads';

const StyledFormControl = styled(FormControl)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const StyledLabel = styled('label')({
  fontSize: '14px',
});

type FocusStatutesOption =
  | { type: 'statute'; optionId: string; label: string; statute: Statute }
  | { type: 'dyad'; optionId: string; label: string; dyad: EnhancementDyad };

export interface DisparityFieldsData {
  statuteIds: number[];
  stageId: number | null;
  typeId: number | null;
  subtypeId: number | null;
  raceId: number | null;
  startDate: Date | null;
  endDate: Date | null;
}

interface DisparityFieldsFormProps {
  caseData?: Case;
  disparityStages?: Array<{ id: number; name: string }>;
  disparityTypes?: Array<{ id: number; name: string; subtypes?: Array<{ id: number; name: string }> }>;
  processedRaces?: Array<{ id: number; name: string }>;
  values: DisparityFieldsData;
  onChange: (field: keyof DisparityFieldsData, value: Date | number | number[] | null) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  minFoundDate?: string | null;
  showInfoBox?: boolean;
  onDateErrorChange?: (hasError: boolean) => void;
  baseStatuteIds?: number[];
  onBaseStatuteChange?: (statuteIds: number[]) => void;
  singleStatuteOnly?: boolean;
  enhancementDyads?: EnhancementDyad[];
  selectedEnhancementDyad?: EnhancementDyad | null;
  onEnhancementDyadChange?: (dyad: EnhancementDyad | null) => void;
}

function DisparityFieldsForm({
  caseData,
  disparityStages,
  disparityTypes,
  processedRaces,
  values,
  onChange,
  minDate,
  maxDate,
  minFoundDate,
  showInfoBox = false,
  onDateErrorChange,
  baseStatuteIds,
  onBaseStatuteChange,
  singleStatuteOnly = false,
  enhancementDyads: dyads = [],
  selectedEnhancementDyad: selectedDyad = null,
  onEnhancementDyadChange,
}: DisparityFieldsFormProps) {
  const [isStatuteSelectOpen, setIsStatuteSelectOpen] = useState(false);

  // Checked only when the start date is the unknown/beginning date (1900-01-01). Mirrors the
  // disparity panel; only shown for existing disparities (showInfoBox)
  const includeUnknownDates = isUnknownStartDate(values.startDate);

  const handleIncludeUnknownDatesToggle = (checked: boolean) => {
    if (checked) {
      // Include unknown dates: switch the start date to the unknown/beginning date (1900-01-01)
      onChange('startDate', UNKNOWN_START_DATE);
    } else if (minFoundDate && isUnknownStartDate(values.startDate)) {
      // Default to the first real found date, but keep any date the user has already entered
      onChange('startDate', parseISO(minFoundDate));
    }
  };

  // Out-of-range / invalid date picker errors block applying.
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);

  useEffect(() => {
    onDateErrorChange?.(!!startDateError || !!endDateError);
  }, [startDateError, endDateError, onDateErrorChange]);

  const effectiveStatuteIds = showInfoBox ? (baseStatuteIds ?? []) : values.statuteIds;
  const isWobblerDisabled = Boolean(
    effectiveStatuteIds.length !== 1 ||
    caseData?.statutes?.find((s) => s.id === effectiveStatuteIds[0] && s.is_wobbler === false),
  );

  const selectedType = disparityTypes?.find((type) => type.id === values.typeId);

  const handleStatuteIdsChange = (ids: number[]) => {
    if (showInfoBox) {
      onBaseStatuteChange?.(ids);
    } else {
      onChange('statuteIds', ids);
    }
  };

  const handleInputChange = (field: keyof DisparityFieldsData) => (e: SelectChangeEvent<number>) => {
    onChange(field, e.target.value as number);
    if (field === 'typeId') {
      onChange('subtypeId', null);
    }
  };

  const handleDyadToggle = (dyad: EnhancementDyad) => {
    const isCurrentlySelected = selectedDyad?.id === dyad.id;
    if (isCurrentlySelected) {
      onEnhancementDyadChange?.(null);
      handleStatuteIdsChange([]);
      onChange('stageId', DisparityStage.ARREST);
      onChange('typeId', DisparityType.PER_CAPITA);
      onChange('subtypeId', null);
    } else {
      onEnhancementDyadChange?.(dyad);
      handleStatuteIdsChange([dyad.baseStatute.id, dyad.enhancementStatute.id]);
      onChange('typeId', DisparityType.SEVERITY);
      onChange('subtypeId', DisparitySubType.ENHANCEMENT);
      if (values.stageId === DisparityStage.ARREST) {
        onChange('stageId', DisparityStage.CHARGING);
      }
    }
  };

  const statuteFocusOptions: Array<Extract<FocusStatutesOption, { type: 'statute' }>> = (caseData?.statutes ?? []).map(
    (s) => ({
      type: 'statute' as const,
      optionId: `s-${s.id}`,
      label: s.display_name,
      statute: s,
    }),
  );

  const dyadFocusOptions: FocusStatutesOption[] = dyads.map((d) => ({
    type: 'dyad' as const,
    optionId: `d-${d.id}`,
    label: d.label,
    dyad: d,
  }));

  const allFocusOptions: FocusStatutesOption[] = [...statuteFocusOptions, ...dyadFocusOptions];

  const focusValue: FocusStatutesOption[] = selectedDyad
    ? [{ type: 'dyad', optionId: `d-${selectedDyad.id}`, label: selectedDyad.label, dyad: selectedDyad }]
    : statuteFocusOptions.filter((o) => effectiveStatuteIds.includes(o.statute.id));

  const handleFocusOptionChange = (_event: React.SyntheticEvent, newValue: FocusStatutesOption[]) => {
    const incomingStatutes = newValue.filter(
      (o): o is Extract<FocusStatutesOption, { type: 'statute' }> => o.type === 'statute',
    );
    const incomingDyads = newValue.filter(
      (o): o is Extract<FocusStatutesOption, { type: 'dyad' }> => o.type === 'dyad',
    );

    // Enter or switch to enhancement dyad mode
    const targetDyad = incomingDyads.find((o) => o.dyad.id !== selectedDyad?.id);
    if (targetDyad) {
      handleDyadToggle(targetDyad.dyad);
      setIsStatuteSelectOpen(false);
      return;
    }

    // Statute change with no enhancement dyad
    if (incomingStatutes.length > 0) {
      if (selectedDyad) handleDyadToggle(selectedDyad);
      handleStatuteIdsChange(incomingStatutes.map((o) => o.statute.id));
      setIsStatuteSelectOpen(false);
      return;
    }

    if (selectedDyad) handleDyadToggle(selectedDyad);
    else handleStatuteIdsChange([]);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Info Box - Only for existing disparities */}
      {showInfoBox && (
        <Box
          sx={{
            backgroundColor: '#fff',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
            padding: '16px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {caseData?.county && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
              <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary', marginBottom: '4px' }}>
                County:
              </Typography>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                {caseData.county.name}
              </Typography>
            </Box>
          )}

          {caseData?.client_race && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
              <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary', marginBottom: '4px' }}>
                Client race:
              </Typography>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                {caseData.client_race.name}
              </Typography>
            </Box>
          )}

          {/* Focus statute chips */}
          {caseData?.statutes && caseData.statutes.length > 0 && (
            <Box sx={{ marginTop: '8px' }}>
              <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary', marginBottom: '8px' }}>
                Focus statute(s):
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px', flexWrap: 'wrap' }}>
                {caseData.statutes.map((statute) => {
                  const isSelected = !selectedDyad && effectiveStatuteIds.includes(statute.id);
                  return (
                    <Box
                      key={statute.id}
                      onClick={() => {
                        if (selectedDyad) {
                          handleDyadToggle(selectedDyad);
                          handleStatuteIdsChange([statute.id]);
                          return;
                        }
                        if (isSelected && effectiveStatuteIds.length === 1) return;
                        const newIds = isSelected
                          ? effectiveStatuteIds.filter((id) => id !== statute.id)
                          : singleStatuteOnly
                            ? [statute.id]
                            : [...effectiveStatuteIds, statute.id];
                        handleStatuteIdsChange(newIds);
                      }}
                      sx={{
                        backgroundColor: 'accentBlue.main',
                        paddingLeft: '4px',
                        paddingRight: '12px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      <Checkbox
                        size='small'
                        checked={isSelected}
                        sx={{
                          color: '#fff',
                          pointerEvents: 'none',
                          '&.Mui-checked': { color: '#fff' },
                        }}
                      />
                      <Typography variant='caption' sx={{ fontWeight: 600 }}>
                        {statute.display_name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Enhancement dyad boxes */}
          {dyads.length > 0 && (
            <Box sx={{ marginTop: '8px' }}>
              <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary', marginBottom: '8px' }}>
                Enhancements:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px', flexWrap: 'wrap' }}>
                {dyads.map((dyad) => {
                  const isSelected = selectedDyad?.id === dyad.id;
                  return (
                    <Box
                      key={dyad.id}
                      onClick={() => handleDyadToggle(dyad)}
                      sx={{
                        backgroundColor: 'accentBlue.main',
                        paddingLeft: '4px',
                        paddingRight: '12px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      <Checkbox
                        size='small'
                        checked={isSelected}
                        sx={{
                          color: '#fff',
                          pointerEvents: 'none',
                          '&.Mui-checked': { color: '#fff' },
                        }}
                      />
                      <Typography variant='caption' sx={{ fontWeight: 600 }}>
                        {dyad.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      )}
      {/* Focus Statute - Autocomplete (new disparity only) */}
      {!showInfoBox && (
        <StyledFormControl>
          <StyledLabel>Focus Statute</StyledLabel>
          <Autocomplete
            multiple
            clearIcon={null}
            popupIcon={null}
            open={isStatuteSelectOpen}
            onOpen={() => setIsStatuteSelectOpen(true)}
            onClose={() => setIsStatuteSelectOpen(false)}
            options={allFocusOptions}
            getOptionLabel={(option) => option.label}
            value={focusValue}
            onChange={handleFocusOptionChange}
            isOptionEqualToValue={(option, value) => option.optionId === value.optionId}
            getOptionDisabled={(option) => {
              if (option.type === 'dyad') return false;
              if (selectedDyad) return false;
              return (
                singleStatuteOnly && effectiveStatuteIds.length >= 1 && !effectiveStatuteIds.includes(option.statute.id)
              );
            }}
            filterOptions={(options, params) => {
              const { inputValue } = params;
              if (!inputValue.trim()) return options;
              const searchTerms = inputValue
                .toLowerCase()
                .split(/\s+/)
                .filter((t) => t.length > 0);
              if (!searchTerms.length) return options;
              return options.filter((option) => {
                const label = option.label.toLowerCase();
                if (option.type === 'dyad') return searchTerms.every((t) => label.includes(t));
                const desc = option.statute.description?.toLowerCase() ?? '';
                return searchTerms.every((t) => label.includes(t) || desc.includes(t));
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant='outlined'
                placeholder={!caseData?.statutes?.length && !dyads.length ? 'Currently N/A' : ''}
                sx={{
                  '& .MuiInputBase-root': {
                    paddingRight: '9px !important',
                  },
                }}
              />
            )}
            slotProps={{
              popper: {
                sx: {
                  '& .MuiAutocomplete-option': {
                    fontSize: '14px',
                  },
                  '& .MuiAutocomplete-noOptions': {
                    fontSize: '14px',
                  },
                },
              },
            }}
            noOptionsText='No statutes found'
            renderOption={(props, option) =>
              option.type === 'dyad' ? (
                <li
                  {...props}
                  key={option.optionId}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}
                  title={`${option.dyad.baseStatute.description} + ${option.dyad.enhancementStatute.description}`}
                >
                  <Typography variant='body2'>{option.label}</Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      fontStyle: 'italic',
                      color: '#718096',
                    }}
                  >
                    Enhancement
                  </Typography>
                </li>
              ) : (
                <li
                  {...props}
                  key={option.optionId}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}
                >
                  {option.label}
                  <br />
                  <Typography
                    variant='body2'
                    sx={{
                      fontStyle: 'italic',
                      color: '#718096',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}
                    title={`${option.label} - ${option.statute.description}`}
                  >
                    {capitalizeFirstLetter(option.statute.description?.toLowerCase() || '')}
                  </Typography>
                </li>
              )
            }
          />
        </StyledFormControl>
      )}
      <StyledFormControl>
        <StyledLabel>Stage</StyledLabel>
        <Select value={values.stageId ?? -1} onChange={handleInputChange('stageId')}>
          {disparityStages?.map((s) => (
            <MenuItem
              key={s.id}
              value={s.id}
              disabled={
                (s.id === DisparityStage.ARREST &&
                  (values.typeId === DisparityType.TRANSITION || values.subtypeId === DisparitySubType.ENHANCEMENT)) ||
                s.id === DisparityStage.SENTENCING
              }
            >
              {s.name}
            </MenuItem>
          ))}
        </Select>
      </StyledFormControl>
      <StyledFormControl>
        <StyledLabel>Disparity type</StyledLabel>
        <Select value={values.typeId ?? -1} onChange={handleInputChange('typeId')}>
          {disparityTypes?.map((t) => (
            <MenuItem
              key={t.id}
              value={t.id}
              disabled={t.id === DisparityType.TRANSITION && values.stageId === DisparityStage.ARREST}
            >
              {t.name}
            </MenuItem>
          ))}
        </Select>
      </StyledFormControl>
      {selectedType?.subtypes && selectedType.subtypes.length > 0 && (
        <StyledFormControl>
          <StyledLabel>Disparity subtype</StyledLabel>
          <Select value={values.subtypeId ?? ''} onChange={handleInputChange('subtypeId')} displayEmpty>
            <MenuItem value={''} disabled>
              Select a subtype
            </MenuItem>
            {selectedType.subtypes.map((st) => (
              <MenuItem
                key={st.id}
                value={st.id}
                disabled={
                  (st.id === DisparitySubType.WOBBLERS && isWobblerDisabled) ||
                  (st.id === DisparitySubType.ENHANCEMENT && !selectedDyad)
                }
              >
                {st.name}
              </MenuItem>
            ))}
          </Select>
        </StyledFormControl>
      )}
      <StyledFormControl>
        <StyledLabel>Race Comparison</StyledLabel>
        <Select value={values.raceId ?? ''} onChange={handleInputChange('raceId')}>
          {processedRaces?.map((race) => (
            <MenuItem key={race.id} value={race.id}>
              {race.name}
            </MenuItem>
          ))}
        </Select>
      </StyledFormControl>
      <StyledFormControl>
        <StyledLabel>Offense date</StyledLabel>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={values.startDate}
              label={showInfoBox && isSameDayDate(values.startDate, minFoundDate) ? 'Earliest found date' : undefined}
              minDate={minDate ?? undefined}
              maxDate={values.endDate ?? maxDate ?? undefined}
              onChange={(newValue) => onChange('startDate', newValue)}
              onError={(error) => setStartDateError(error)}
              slotProps={{
                textField: {
                  slotProps: {
                    input: {
                      sx: {
                        fontSize: '14px',
                      },
                    },
                    inputLabel: {
                      sx: {
                        fontSize: '14px',
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>{' '}
          -{' '}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={values.endDate}
              minDate={values.startDate ?? minDate ?? undefined}
              onChange={(newValue) => onChange('endDate', newValue)}
              onError={(error) => setEndDateError(error)}
              slotProps={{
                textField: {
                  slotProps: {
                    input: {
                      sx: {
                        fontSize: '14px',
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        {showInfoBox && (
          <FormControlLabel
            sx={{ m: 0 }}
            disabled={!minFoundDate}
            control={
              <Checkbox
                size='small'
                sx={{ py: 0 }}
                disableRipple
                checked={includeUnknownDates}
                onChange={(event) => handleIncludeUnknownDatesToggle(event.target.checked)}
              />
            }
            label={<Typography sx={{ fontSize: '12px' }}>Include unknown dates</Typography>}
          />
        )}
      </StyledFormControl>
    </Box>
  );
}

export default DisparityFieldsForm;
